import { useDeviationAnalysis } from 'ClientPortal/src/pages/Assets/AssetDetails/useDeviationAnalysis'
import {
    CardItem,
    DeviationAnalysisExportCard,
    DeviationAnalysisWidget,
    getDeviationAnalysisExportCardCopy,
    getDeviationAnalysisExportCopy,
    getDeviationAnalysisWidgetCopy,
    GroupedValuationDeviationOptions,
    MultiStructuresInfoBox,
    PageGridItem,
    tokens,
    ValuationDeviationOptions,
} from 'ComponentLibrary'
import { Stack } from '@mui/material'
import { ValuationStructure } from 'ComponentLibrary/src/widgets/AssetInfoBoxes/types'
import { isEmpty } from 'lodash'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'
import { getMultiStructuresInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/multiStructuresInfoBoxCopy'

type DeviationAnalysisProps = {
    valuationOptions?: ValuationDeviationOptions
    groupedValuationOptions?: GroupedValuationDeviationOptions
    hasGroupedValuations?: boolean
    selectedValuation?: ExistingValuation
    assetTitle: string
    structures: ValuationStructure[]
    locale: string
}

export function DeviationAnalysis({
    valuationOptions,
    groupedValuationOptions,
    hasGroupedValuations,
    selectedValuation,
    structures,
    assetTitle,
    locale,
}: DeviationAnalysisProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tDeviationAnalysisWidget } = useTranslation('terminology', { keyPrefix: 'common.deviationAnalysisWidget' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const {
        deviationAnalysis,
        isDeviationAnalysisPending,
        baseValuation,
        comparisonValuation,
        setComparisonValuationId,
        setBaseValuationId,
    } = useDeviationAnalysis({
        valuationOptions,
        selectedValuation,
        lockedBaseValuation: true,
    })
    const deviationAnalysisExportCardCopy = getDeviationAnalysisExportCardCopy(tExport)
    const deviationAnalysisExportCopy = getDeviationAnalysisExportCopy(tCommon, tExport)
    const deviationAnalysisWidgetCopy = getDeviationAnalysisWidgetCopy(
        deviationAnalysisExportCopy,
        tDeviationAnalysisWidget,
        comparisonValuation?.valuationDate ?? comparisonValuation?.date
    )
    const multiStructuresInfoBoxCopy = getMultiStructuresInfoBoxCopy(tCommon)

    return (
        <>
            <PageGridItem columnSpan={4} columnSpanXs={4}>
                <CardItem height="fit-content">
                    <DeviationAnalysisWidget
                        deviationAnalysis={deviationAnalysis}
                        isDeviationAnalysisPending={isDeviationAnalysisPending}
                        valuationOptions={valuationOptions}
                        groupedValuationOptions={groupedValuationOptions}
                        hasGroupedValuations={hasGroupedValuations}
                        setBaseValuationId={setBaseValuationId}
                        setComparisonValuationId={setComparisonValuationId}
                        baseValuation={baseValuation}
                        comparisonValuation={comparisonValuation}
                        copy={deviationAnalysisWidgetCopy}
                        locale={locale}
                        lockBaseValuation
                    />
                </CardItem>
            </PageGridItem>

            <PageGridItem columnSpan={2} columnSpanXs={2}>
                <Stack gap={tokens.SizingSpacing300} position="sticky" top={tokens.SizingSpacing200} height="fit-content">
                    <DeviationAnalysisExportCard
                        baseValuation={baseValuation}
                        comparisonValuation={comparisonValuation}
                        deviationAnalysis={deviationAnalysis}
                        assetTitle={assetTitle}
                        copy={deviationAnalysisExportCardCopy}
                        exportCopy={deviationAnalysisExportCopy}
                        locale={locale}
                    />
                    {!isEmpty(structures) && <MultiStructuresInfoBox structures={structures} copy={multiStructuresInfoBoxCopy} />}
                </Stack>
            </PageGridItem>
        </>
    )
}
